import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { X } from "lucide-react";
import { useState, useEffect } from "react";
import { motion } from "framer-motion";

interface BannerData {
  id: string;
  message: string;
  scroll_speed: string;
  background_color: string;
  text_color: string;
  link_url: string | null;
  link_text: string | null;
}

export const NotificationBanner = () => {
  const [dismissed, setDismissed] = useState<string[]>([]);

  // Load dismissed banners from session storage
  useEffect(() => {
    const stored = sessionStorage.getItem("dismissed_banners");
    if (stored) {
      setDismissed(JSON.parse(stored));
    }
  }, []);

  const { data: banners } = useQuery({
    queryKey: ["notification-banners"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("cms_notification_banner")
        .select("*")
        .eq("is_active", true)
        .order("display_order", { ascending: true });
      
      if (error) throw error;
      return data as BannerData[];
    },
  });

  const handleDismiss = (id: string) => {
    const newDismissed = [...dismissed, id];
    setDismissed(newDismissed);
    sessionStorage.setItem("dismissed_banners", JSON.stringify(newDismissed));
  };

  const activeBanners = banners?.filter((b) => !dismissed.includes(b.id)) || [];

  if (activeBanners.length === 0) return null;

  const getSpeedClass = (speed: string) => {
    switch (speed) {
      case "slow": return "animate-[scroll_30s_linear_infinite]";
      case "fast": return "animate-[scroll_10s_linear_infinite]";
      default: return "animate-[scroll_20s_linear_infinite]";
    }
  };

  return (
    <motion.div 
      initial={{ y: -50, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      className="fixed top-0 left-0 right-0 z-[60] w-full"
    >
      {activeBanners.map((banner, index) => (
        <motion.div
          key={banner.id}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: index * 0.1 + 0.3, duration: 0.3 }}
          className="relative bg-primary text-primary-foreground py-3 overflow-hidden"
        >
          <div className="flex items-center">
            <div className="flex-1 overflow-hidden">
              <div className={`whitespace-nowrap ${getSpeedClass(banner.scroll_speed)}`}>
                <span className="inline-block px-4">
                  {banner.message}
                  {banner.link_url && banner.link_text && (
                    <a
                      href={banner.link_url}
                      className="ml-2 underline hover:no-underline font-semibold animate-pulse hover:animate-none transition-all hover:text-primary-foreground/80"
                    >
                      {banner.link_text}
                    </a>
                  )}
                </span>
                <span className="inline-block px-4">
                  {banner.message}
                  {banner.link_url && banner.link_text && (
                    <a
                      href={banner.link_url}
                      className="ml-2 underline hover:no-underline font-semibold animate-pulse hover:animate-none transition-all hover:text-primary-foreground/80"
                    >
                      {banner.link_text}
                    </a>
                  )}
                </span>
              </div>
            </div>
            <button
              onClick={() => handleDismiss(banner.id)}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-primary-foreground/10 rounded transition-colors"
              aria-label="Dismiss banner"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </motion.div>
      ))}
    </motion.div>
  );
};

export const useBannerHeight = () => {
  const [height, setHeight] = useState(0);
  const [dismissed, setDismissed] = useState<string[]>([]);

  useEffect(() => {
    const stored = sessionStorage.getItem("dismissed_banners");
    if (stored) {
      setDismissed(JSON.parse(stored));
    }

    const handleStorage = () => {
      const stored = sessionStorage.getItem("dismissed_banners");
      if (stored) {
        setDismissed(JSON.parse(stored));
      }
    };

    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  const { data: banners } = useQuery({
    queryKey: ["notification-banners"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("cms_notification_banner")
        .select("*")
        .eq("is_active", true)
        .order("display_order", { ascending: true });
      
      if (error) throw error;
      return data as BannerData[];
    },
  });

  useEffect(() => {
    const activeBanners = banners?.filter((b) => !dismissed.includes(b.id)) || [];
    // Each banner is approximately 44px (py-3 + text)
    setHeight(activeBanners.length * 44);
  }, [banners, dismissed]);

  return height;
};
